import React, { useContext, useEffect, useMemo } from 'react'
import { useOfferList } from 'hooks/Offers/useOfferList'
import HeroOfferLoadingCarousel from 'home/components/OfferCarousels/HeroOfferLoadingCarousel'
import { LFSEOfferType } from 'home/components/LookingForSomethingElse/constants'
import LayoutContainer from 'components/Common/LayoutContainer'
import PaletteProvider from 'contexts/PaletteProvider'
import Heading from 'components/Luxkit/Typography/Heading'
import styled from 'styled-components'
import TextLink from 'components/Luxkit/TextLink'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/v2/OfferListEventsContext'
import Group from 'components/utils/Group'
import { EmptyArray, take } from 'lib/arrayUtils'

interface Props {
  filters?: App.OfferListFilters;
  random?: boolean;
  maxOffers: number;
  tileStyle: 'frontpage' | 'search' | 'hero';
}

const ExclusiveOfferContainer = styled.div`
  padding: ${rem(64)} 0 ${rem(32)};
  background-color: ${({ theme }) => theme.palette.primary.normal};

  ${mediaQueryUp.tablet} {
    padding: ${rem(96)} 0  ${rem(40)};
  }
`

const TopExclusiveHeaderGrid = styled.div`
  display: grid;
  align-items: center;
  grid-template-areas:
    "title link";
  grid-template-columns: 1fr max-content;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-gap: ${rem(16)};
  }

  > .title-area {
    grid-area: title;
  }

  > .link-area {
    grid-area: link;
  }
`

function TourV2ExclusiveOfferCarousel(props: Props) {
  const {
    filters,
    random,
    tileStyle,
    maxOffers,
  } = props
  const [offerList] = useOfferList(filters, { randomize: random })

  const onEvent = useContext(OfferListEventsContext)
  const [inViewRef, inView] = useInView({ triggerOnce: true, threshold: 0.5 })

  const offerIds = useMemo(() => take(offerList.offerIds, maxOffers) || EmptyArray, [offerList.offerIds, maxOffers])

  useEffect(() => {
    if (inView && onEvent) {
      onEvent(OfferListEvents.impression)
    }
  }, [inView, onEvent])

  return (
    <ExclusiveOfferContainer ref={inViewRef}>
      <Group direction="vertical" gap={24} tabletGap={32}>
        <LayoutContainer>
          <PaletteProvider type="inverse">
            <TopExclusiveHeaderGrid>
              <Heading className="title-area" variant="heading2" align="start" >Today's top <i>exclusive</i> offers</Heading>
              <TextLink className="link-area" size="medium" to="/tours/exclusive">View all</TextLink>
            </TopExclusiveHeaderGrid>
          </PaletteProvider>
        </LayoutContainer>
        <HeroOfferLoadingCarousel
          offerIds={offerIds}
          tileStyle={tileStyle}
          hasLFSETile
          link="/tours/exclusive"
          offerType={LFSEOfferType.Tours}
        />
      </Group>
    </ExclusiveOfferContainer>
  )
}

TourV2ExclusiveOfferCarousel.defaultProps = {
  filters: {},
  tileStyle: 'frontpage',
  maxOffers: 15,
}

export default TourV2ExclusiveOfferCarousel
